import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { BookmarkBorder, Bookmark, Lock as LockIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getCardImageUrl, getArtworkTitle } from '../utils/artworkApi';
import { formatCatalogNumber, formatCreationDateThumbnail } from '../utils/catalogFormatters';
import { buildArtworkDetailLink } from '../utils/artworkDetailLink';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { ThumbnailImage } from './ThumbnailImage';
import type { ArtworkItem } from './search/artworkItem';

type GridVariant = 'grid' | 'image';

interface SearchResultsGridProps {
  artworks: ArtworkItem[];
  /** Explicit CSS grid-template-columns override. If omitted, derived from `variant`. */
  gridColumns?: string;
  /** Layout variant per design spec: 'grid' (220px) or 'image' (dense 180px). */
  variant?: GridVariant;
  isBookmarked: (id: string) => boolean;
  toggleBookmark: (id: string) => void;
  currentUserId: string | null;
  /** Optional permission check — returns true if the current user can edit the given artwork */
  canEdit?: (artworkId: string, ownerId: string) => boolean;
}

const VARIANT_COLUMNS: Record<GridVariant, string> = {
  grid: 'repeat(auto-fill, minmax(220px, 1fr))',
  image: 'repeat(auto-fill, minmax(180px, 1fr))',
};

export const SearchResultsGrid = ({
  artworks,
  gridColumns,
  variant = 'grid',
  isBookmarked,
  toggleBookmark,
  currentUserId,
  canEdit,
}: SearchResultsGridProps) => {
  const resolvedColumns = gridColumns ?? VARIANT_COLUMNS[variant];
  const theme = useTheme();
  const { isDelegateOf } = useActiveCatalog();

  if (artworks.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          minHeight: { xs: 400, sm: 600, md: 500, lg: 400 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: { xs: 1, sm: 0 },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No objects found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search terms
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="search-results-grid"
      data-variant={variant}
      sx={{
        bgcolor: theme.palette.divider,
        border: `1px solid ${theme.palette.divider}`,
        display: 'grid',
        gridTemplateColumns: resolvedColumns,
        gridAutoRows: 'auto',
        gap: '1px',
        mx: { xs: 1, sm: 0 },
      }}
    >
      {artworks.map((artworkItem, index) => {
        const thumbnailUrl =
          artworkItem.coverImageUrl || getCardImageUrl(artworkItem);
        const title = getArtworkTitle(artworkItem);
        const creator = artworkItem.creator || 'Unknown Artist';

        const userCanEdit = canEdit
          ? canEdit(artworkItem.artworkId, artworkItem.userId)
          : false;

        const detailLink = buildArtworkDetailLink(
          { artworkId: artworkItem.artworkId, userId: artworkItem.userId },
          { currentUserId, isDelegateOf }
        );

        return (
          <Box
            key={artworkItem.artworkId}
            sx={{
              p: { xs: 1, sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              width: '100%',
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              '&:hover .bookmark-btn, &:focus-within .bookmark-btn': {
                opacity: 1,
              },
              '@media (hover: none)': {
                '.bookmark-btn': { opacity: 1 },
              },
            }}
          >
            {/* Bookmark button — hover-revealed (#1099); always visible when bookmarked */}
            <IconButton
              className="bookmark-btn"
              size="small"
              aria-label={`Bookmark ${title}`}
              aria-pressed={isBookmarked(artworkItem.artworkId)}
              onClick={(e) => {
                e.stopPropagation();
                toggleBookmark(artworkItem.artworkId);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                color: isBookmarked(artworkItem.artworkId)
                  ? 'primary.main'
                  : 'text.secondary',
                opacity: isBookmarked(artworkItem.artworkId) ? 1 : 0,
                transition: 'opacity 150ms ease-in-out',
                zIndex: 2,
              }}
            >
              {isBookmarked(artworkItem.artworkId) ? (
                <Bookmark fontSize="small" />
              ) : (
                <BookmarkBorder fontSize="small" />
              )}
            </IconButton>

            {/* Edit button — only shown when the user has edit permission */}
            {userCanEdit && (
              <Tooltip title="Edit Object">
                <IconButton
                  component={Link}
                  to={`/upload-object?edit=${artworkItem.artworkId}`}
                  aria-label="Edit"
                  size="small"
                  color="primary"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                    zIndex: 2,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Box
              component={Link}
              to={detailLink.to}
              state={{
                itemIds: artworks.map(a => a.artworkId),
                currentIndex: index,
                routePrefix: detailLink.routePrefix,
                from: 'search',
              }}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '120px', sm: '150px', md: '180px', lg: '200px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                <ThumbnailImage
                  src={thumbnailUrl}
                  alt={title}
                />
              </Box>

              {/* Private lock — shown to the owner on their own private items only (#1095 search-results extension: globe removed, lock retained as the "attention" signal) */}
              {currentUserId !== null &&
                artworkItem.userId === currentUserId &&
                artworkItem.visibility === 'private' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      zIndex: 1,
                    }}
                  >
                    <LockIcon
                      data-testid="visibility-icon-private"
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                  </Box>
                )}

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  px: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontStyle: 'italic',
                    mb: 0.5,
                    fontSize: {
                      xs: '0.7rem',
                      sm: '0.8rem',
                      md: '0.9rem',
                    },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontSize: {
                      xs: '0.6rem',
                      sm: '0.7rem',
                      md: '0.8rem',
                    },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {creator}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    fontSize: {
                      xs: '0.6rem',
                      sm: '0.7rem',
                      md: '0.8rem',
                    },
                  }}
                >
                  {formatCreationDateThumbnail(artworkItem.creationYear) || ''}
                </Typography>
                {formatCatalogNumber(artworkItem) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: {
                        xs: '0.5rem',
                        sm: '0.6rem',
                        md: '0.7rem',
                      },
                    }}
                  >
                    {formatCatalogNumber(artworkItem)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
